import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ErrorMessageProps {
  /**
   * Main error title
   * @default 'An error occurred'
   */
  title?: string;
  /**
   * Error message or Error object
   */
  message?: string | Error;
  /**
   * Additional details about the error
   */
  details?: string;
  /**
   * Whether to show a retry button
   * @default false
   */
  showRetry?: boolean;
  /**
   * Callback when retry button is clicked
   */
  onRetry?: () => void;
  /**
   * Minimum height for the container
   * @default '50vh'
   */
  minHeight?: string | number;
  /**
   * Whether to show the error inline (no container)
   * @default false
   */
  inline?: boolean;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  title = 'An error occurred',
  message,
  details,
  showRetry = false,
  onRetry,
  minHeight = '50vh',
  inline = false
}) => {
  const errorMessage = message instanceof Error ? message.message : message;

  const content = (
    <>
      <ErrorOutlineIcon
        sx={{
          fontSize: inline ? 40 : 64,
          color: 'error.main',
          mb: 2
        }}
      />
      <Typography
        variant={inline ? 'body1' : 'h6'}
        color="error.main"
        gutterBottom
      >
        {title}
      </Typography>
      {errorMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: details || showRetry ? 2 : 0 }}
        >
          {errorMessage}
        </Typography>
      )}
      {details && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: showRetry ? 2 : 0 }}
        >
          {details}
        </Typography>
      )}
      {showRetry && onRetry && (
        <Button variant="contained" onClick={onRetry} sx={{ mt: 2 }}>
          Retry
        </Button>
      )}
    </>
  );

  if (inline) {
    return <Box sx={{ textAlign: 'center', py: 2 }}>{content}</Box>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight,
        textAlign: 'center',
        px: 2
      }}
    >
      {content}
    </Box>
  );
};
